import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import AuthService from "src/services/authService";
import { withRequiredRole } from "src/ui/navigation/utils/withRequiredRole";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { deleteGroup, moveGroupLicenses, revokeGroupLicenses } from "@admin/api/group";
import { createGroup } from "@dashboard/devices/api/groups";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { ConfirmDialog } from "@devices/components/shared";
import { Box, DialogContentText, List, ListItem, Theme, Typography } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { DangerButton, PrimaryButton } from "@shared/CustomButton";
import { NumberField } from "@shared/form/NumberField";
import { SwitchField } from "@shared/form/SwitchField";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

type CreateFormValues = { name: string; subscriptionId: string; parentGroupId: string };
type MergeFormValues = { sourceGroupId: string; targetGroupId: string };
type DeleteFormValues = { groupId: string; force: boolean };
type RevokeFormValues = { groupId: string; reason: string };
type DeleteConfirmState = { groupId: number; force: boolean };

const createInitialValues: CreateFormValues = { name: "", subscriptionId: "", parentGroupId: "" };
const mergeInitialValues: MergeFormValues = { sourceGroupId: "", targetGroupId: "" };
const deleteInitialValues: DeleteFormValues = { groupId: "", force: false };
const revokeInitialValues: RevokeFormValues = { groupId: "", reason: "" };

const forceWarningTargets = ["subgroups", "licenses", "devices", "products", "files"] as const;

const idSchema = z.string().regex(/^\d+$/, i18next.t("pages:adminGroupsPage.validation.id"));
const optionalIdSchema = z.union([z.literal(""), idSchema]);

const createSchema = z.object({
    name: z.string().trim().min(1, i18next.t("pages:adminGroupsPage.validation.groupName")),
    subscriptionId: idSchema,
    parentGroupId: optionalIdSchema,
});
const mergeSchema = z
    .object({ sourceGroupId: idSchema, targetGroupId: idSchema })
    .refine((values) => values.sourceGroupId !== values.targetGroupId, {
        message: i18next.t("pages:adminGroupsPage.validation.sameGroup"),
        path: ["targetGroupId"],
    });
const deleteSchema = z.object({ groupId: idSchema, force: z.boolean() });
const revokeSchema = z.object({ groupId: idSchema, reason: z.string() });

const useStyles = makeStyles()((theme: Theme) => ({
    section: { paddingBottom: 0 },
    formBox: { display: "flex", flexDirection: "column", gap: theme.spacing(2), padding: theme.spacing(2, 0) },
    buttonBox: { display: "flex", justifyContent: "flex-end" },
    warningList: {
        listStyleType: "disc",
        paddingLeft: theme.spacing(3),
        margin: 0,
    },
}));

const AdminGroupsPage = () => {
    const { t } = useTranslation(["pages", "common", "modals"]);
    const { classes } = useStyles();

    const isAdmin = AuthService.isUserAdmin();
    const adminOnlyTooltip = isAdmin ? "" : t("pages:adminGroupsPage.action.adminOnly");

    const [deleteConfirm, setDeleteConfirm] = React.useState<DeleteConfirmState | null>(null);
    const deleteFormRef = React.useRef<FormApi<DeleteFormValues> | null>(null);

    const validateCreate = useValidationSchema(createSchema);
    const validateMerge = useValidationSchema(mergeSchema);
    const validateDelete = useValidationSchema(deleteSchema);
    const validateRevoke = useValidationSchema(revokeSchema);

    const onCreate = async (values: CreateFormValues, form: FormApi<CreateFormValues>) => {
        await createGroup({
            name: values.name.trim(),
            description: "",
            subscriptionId: Number(values.subscriptionId),
            parentId: values.parentGroupId ? Number(values.parentGroupId) : null,
        })
            .then(() => {
                showSuccessToast(t("pages:adminGroupsPage.create.toast.success"));
                form.restart();
            })
            .catch(({ message = t("pages:adminGroupsPage.create.toast.error") }) => showErrorToast(message));
    };

    const onMerge = async (values: MergeFormValues, form: FormApi<MergeFormValues>) => {
        await moveGroupLicenses({
            sourceId: Number(values.sourceGroupId),
            targetGroupId: Number(values.targetGroupId),
        })
            .then(() => {
                showSuccessToast(t("pages:adminGroupsPage.merge.toast.success"));
                form.restart();
            })
            .catch(({ message = t("pages:adminGroupsPage.merge.toast.error") }) => showErrorToast(message));
    };

    const onDelete = (values: DeleteFormValues, form: FormApi<DeleteFormValues>) => {
        deleteFormRef.current = form;
        setDeleteConfirm({ groupId: Number(values.groupId), force: values.force });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirm) {
            return;
        }

        await deleteGroup({ id: deleteConfirm.groupId, force: deleteConfirm.force })
            .then(() => {
                showSuccessToast(t("modals:adminDeleteGroup.toast.success"));
                deleteFormRef.current?.restart();
            })
            .catch(({ message = t("modals:adminDeleteGroup.toast.error") }) => showErrorToast(message));
    };

    const onRevoke = async (values: RevokeFormValues, form: FormApi<RevokeFormValues>) => {
        await revokeGroupLicenses({ id: Number(values.groupId), reason: values.reason })
            .then(() => {
                showSuccessToast(t("pages:adminGroupsPage.revoke.toast.success"));
                form.restart();
            })
            .catch(({ message = t("pages:adminGroupsPage.revoke.toast.error") }) => showErrorToast(message));
    };

    return (
        <PageContainer fullWidth={false}>
            <Form<CreateFormValues>
                onSubmit={onCreate}
                initialValues={createInitialValues}
                validate={validateCreate}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminGroupsPage.create.title")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <TextField name="name" label={t("pages:adminGroupsPage.input.groupName")} />
                                <NumberField name="subscriptionId" label={t("pages:adminGroupsPage.input.subscriptionId")} />
                                <NumberField name="parentGroupId" label={t("pages:adminGroupsPage.input.parentGroupId")} />
                                <Typography variant="body2" color="text.secondary">
                                    {t("pages:adminGroupsPage.create.parentGroupHelper")}
                                </Typography>
                            </Box>
                            <Box className={classes.buttonBox}>
                                <PrimaryButton
                                    type="submit"
                                    disabled={!isAdmin || submitting}
                                    loading={submitting}
                                    tooltipProps={{ title: adminOnlyTooltip }}
                                >
                                    {t("common:button.create")}
                                </PrimaryButton>
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />

            <Form<MergeFormValues>
                onSubmit={onMerge}
                initialValues={mergeInitialValues}
                validate={validateMerge}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminGroupsPage.merge.title")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <Typography>{t("pages:adminGroupsPage.merge.description")}</Typography>
                                <NumberField name="sourceGroupId" label={t("pages:adminGroupsPage.input.sourceGroupId")} />
                                <NumberField name="targetGroupId" label={t("pages:adminGroupsPage.input.targetGroupId")} />
                            </Box>
                            <Box className={classes.buttonBox}>
                                <PrimaryButton type="submit" disabled={submitting} loading={submitting}>
                                    {t("pages:adminGroupsPage.merge.button")}
                                </PrimaryButton>
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />

            <Form<DeleteFormValues>
                onSubmit={onDelete}
                initialValues={deleteInitialValues}
                validate={validateDelete}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminGroupsPage.delete.title")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <Typography>{t("pages:adminGroupsPage.delete.description")}</Typography>
                                <Box>
                                    <SwitchField name="force" label={t("pages:adminGroupsPage.delete.forceLabel")} />
                                </Box>
                                <NumberField name="groupId" label={t("pages:adminGroupsPage.input.groupId")} />
                            </Box>
                            <Box className={classes.buttonBox}>
                                <DangerButton
                                    type="submit"
                                    disabled={!isAdmin || submitting}
                                    loading={submitting}
                                    tooltipProps={{ title: adminOnlyTooltip }}
                                >
                                    {t("common:button.delete")}
                                </DangerButton>
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />

            <Form<RevokeFormValues>
                onSubmit={onRevoke}
                initialValues={revokeInitialValues}
                validate={validateRevoke}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminGroupsPage.revoke.title")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <NumberField name="groupId" label={t("pages:adminGroupsPage.input.groupId")} />
                                <TextField name="reason" label={t("pages:adminGroupsPage.input.reason")} />
                            </Box>
                            <Box className={classes.buttonBox}>
                                <DangerButton
                                    type="submit"
                                    disabled={!isAdmin || submitting}
                                    loading={submitting}
                                    tooltipProps={{ title: adminOnlyTooltip }}
                                >
                                    {t("pages:adminGroupsPage.revoke.button")}
                                </DangerButton>
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />

            <ConfirmDialog
                dangerButton
                open={deleteConfirm !== null}
                title={t("modals:adminDeleteGroup.title")}
                primaryActionText={t("common:button.delete")}
                onConfirm={handleDeleteConfirm}
                onClose={() => setDeleteConfirm(null)}
            >
                <DialogContentText color="text.primary">
                    <Trans
                        ns="modals"
                        i18nKey="adminDeleteGroup.description"
                        values={{ groupId: deleteConfirm?.groupId }}
                        components={{ strong: <strong /> }}
                    />
                </DialogContentText>
                {deleteConfirm?.force ? (
                    <CustomAlert severity="error" title={t("modals:adminDeleteGroup.forceWarning.title")}>
                        <List className={classes.warningList}>
                            {forceWarningTargets.map((target) => (
                                <ListItem key={target} sx={{ display: "list-item", paddingY: 0.25 }}>
                                    <Typography variant="body2">
                                        {t(`modals:adminDeleteGroup.forceWarning.targets.${target}`)}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </CustomAlert>
                ) : null}
            </ConfirmDialog>
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminGroupsPage, {
        title: i18next.t("pages:adminGroupsPage.metaTitle"),
        description: i18next.t("pages:adminGroupsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole, EmteriaRoles.EmteriaSalesRole],
);
